import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type BrandingData from '../interfaces/BrandingData';
import { keycloakService } from '../lib/keycloak';
import { useAppStore } from '../store/store';
import { getCompanyHash } from '../utils/utils';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '../components/Common/LoadingOverlay';

interface BrandingContextType {
  brandingData: BrandingData | null;
  getBrandingData: (withLoading?: boolean) => Promise<void>;
  isLoadingBrandingData: boolean;
}

export const BrandingContext = createContext<BrandingContextType>({
  brandingData: null,
  getBrandingData: async () => {},
  isLoadingBrandingData: false,
});

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [isLoadingBrandingData, setIsLoadingBrandingData] = useState(true);
  const [brandingData, setBrandingData] = useState<BrandingData | null>(null);
  const setBrandingDataStore = useAppStore((s) => s.setBrandingData);
  const { t } = useTranslation();

  /**
   * Get the branding data from the storage or from the server
   */
  const getBrandingData = useCallback(async (withLoading = true) => {
    if (withLoading) {
      setIsLoadingBrandingData(true);
    }
    try {
      // const existing = useAppStore.getState().brandingData;

      // if (existing) {
      //   setBrandingData(existing);
      //   if (existing.realm) {
      //     keycloakService.setRealm(existing.realm);
      //   }
      //   return;
      // }

      const hash = await getCompanyHash();
      const url = [
        import.meta.env.VITE_APP_AZURE_STORAGE_URL,
        import.meta.env.VITE_APP_AZURE_RESOURCES_CONTAINER,
        hash.toUpperCase(),
        'branding.json',
      ].join('/');

      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Fetch branding failed');
      const data: BrandingData = await res.json();

      setBrandingData(data);
      setBrandingDataStore(data);
      if (data.realm) {
        keycloakService.setRealm(data.realm);
      }
    } catch (err) {
      console.error('Error loading brandingData:', err);
      setBrandingData(null);
    } finally {
      setIsLoadingBrandingData(false);
    }
  }, []);

  /**
   * Context value
   */
  const contextValue = useMemo(() => {
    return {
      brandingData,
      getBrandingData,
      isLoadingBrandingData,
    };
  }, [brandingData, getBrandingData, isLoadingBrandingData]);

  useEffect(() => {
    getBrandingData();
  }, [getBrandingData]);

  return (
    <BrandingContext.Provider value={contextValue}>
      <LoadingOverlay label={t('common.loading')} isVisible={isLoadingBrandingData} />

      {!isLoadingBrandingData && children}
    </BrandingContext.Provider>
  );
};
